import {ChangeDetectorRef, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {MAT_DATE_FORMATS} from '@angular/material/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ReferenceBookResponse} from '@npck/af-frontend-api';
import {rxSpinner, Spinner, TIYN_TENGE_SCALE_FACTOR} from '@npck/libs/commons';
import {CheckboxComponent} from '@npck/libs/ui-kit';
import {isEqual} from 'lodash-es';
import {debounceTime, distinctUntilChanged, filter, Observable, startWith, Subscription, take} from 'rxjs';
import {DictionaryHolderService} from '../../../../domain/dictionary-holder.service';
import {DATE_FORMATS, DATE_TIME_FORMATS} from '../../../../infra/date-time.adapter';
import {isAnyFormFillStarted} from '../../../../utils/form.utils';
import {DateForm} from '../../../date-picker-field/date-picker-field.component';
import {DateRangeForm} from '../../../date-range-picker-field/date-range-picker-field.component';
import {
    AccountBalanceRange,
    AmountRange,
    FilterAsyncField,
    FilterDateTimeRange,
    FilterDictionaryField,
    FilterEventForm,
    FilterField,
    FilterFormValue,
    FilterSelectField,
} from '../filter.component';

const SEARCHABLE_FIELD_VALUES_LENGTH = 10;

@Component({
    templateUrl: 'filter-dialog.component.html',
    styleUrls: ['filter-dialog.component.scss'],
    providers: [
        {provide: MAT_DATE_FORMATS, useValue: DATE_TIME_FORMATS},
    ],
    standalone: false,
})
export class FilterDialogComponent implements OnInit, OnDestroy {
    Spinner = Spinner;
    SEARCHABLE_FIELD_VALUES_LENGTH = SEARCHABLE_FIELD_VALUES_LENGTH;
    DATE_TIME_FORMATS = DATE_TIME_FORMATS;
    DATE_FORMATS = DATE_FORMATS;
    form: FormGroup<FilterEventForm>;

    fieldOriginalValueHolder: Record<string, any[]> = {};
    formattedDisplayedDate: string = '';
    subscriptionsHolder: Subscription[] = [];
    searchControls: Record<string, FormControl<string>> = {};
    private currencyDict: ReferenceBookResponse[] = [];
    private initialFormValue: FilterFormValue;

    constructor(
        @Inject(MAT_DIALOG_DATA) public dialogData: FilterDialogData,
        private dialogRef: MatDialogRef<FilterDialogComponent>,
        private formBuilder: FormBuilder,
        private dictionaryHolderService: DictionaryHolderService,
        private cdr: ChangeDetectorRef,
    ) {
        this.dictionaryHolderService.getReference('currency').subscribe(items => {
            this.currencyDict = items;
        });
    }

    get hasFormValues(): boolean {
        if (!this.form) {
            return false;
        }
        return Object.values(this.searchControls).some(control => !!control.value)
            || isAnyFormFillStarted(this.form);
    }

    get isFormChanged(): boolean {
        if (!this.form || this.initialFormValue === undefined) {
            return false;
        }
        return !isEqual(this.initialFormValue, this.form.getRawValue());
    }

    removeDotTransform = (value: string | number): any => {
        return value ? Number(String(value).replace('.', '')) : null;
    };

    ngOnInit(): void {
        this.form = this.formBuilder.group({});
        this.dialogData.fields.forEach(field => {
            const predefinedValue = this.dialogData.filter[field.name];
            const filteredValues = predefinedValue && Object.keys(predefinedValue).filter(key => predefinedValue[key]);
            if (this.isFilterDictionaryField(field)) {
                this.loadFilterValues(
                    field,
                    this.dictionaryHolderService.getReference(field.dictionaryName),
                    filteredValues,
                    ['code', 'code1', 'code2', 'name_ru', 'name_en', 'name_kz'],
                    field.filterBy,
                );
            } else if (this.isFilterAsyncField(field)) {
                this.loadFilterValues(field, field.request, filteredValues, ['code', 'name_ru']);
            } else if (this.isFilterSelectField<any>(field)) {
                const group = this.formBuilder.group({});
                field.values.forEach(value => {
                    const selectedValue = !!(filteredValues?.length && filteredValues.includes(value));
                    group.addControl(value, new FormControl(selectedValue || null));
                });
                this.form.addControl(field.name, group);
            } else if (field.type === 'dateRange') {
                if (field.name === 'dateRange' || field.name === 'deletedDateRange') {
                    const group: FormGroup<DateRangeForm> = this.formBuilder.group({});
                    group.addControl('dateFrom', new FormControl<Date>(predefinedValue?.dateFrom));
                    group.addControl('dateTo', new FormControl<Date>(predefinedValue?.dateTo));
                    this.form.addControl(field.name, group);
                } else {
                    const rangeGroup: FormGroup<FilterDateTimeRange> = this.formBuilder.group({});
                    const startDateGroup = this.buildDateForm();
                    const endDateGroup = this.buildDateForm();
                    predefinedValue?.startDate && startDateGroup.setValue(predefinedValue?.startDate);
                    predefinedValue?.endDate && endDateGroup.setValue(predefinedValue?.endDate);
                    rangeGroup.addControl('startDate', startDateGroup);
                    rangeGroup.addControl('endDate', endDateGroup);

                    this.form.addControl(field.name, rangeGroup);
                }
            } else if (field.type === 'date') {
                const group = this.formBuilder.group<DateForm>({date: new FormControl<Date>(predefinedValue?.date)});
                this.form.addControl(field.name, group);
            } else if (field.type === 'amountRange') {
                const group: FormGroup<AmountRange> = this.formBuilder.group({});
                group.addControl('from', new FormControl<number>(this.tiynToTenge(predefinedValue?.from)));
                group.addControl('to', new FormControl<number>(this.tiynToTenge(predefinedValue?.to)));
                group.addControl('currency', new FormControl<string>(predefinedValue?.currency));
                this.form.addControl(field.name, group);
            } else if (field.type === 'accountBalanceRange') {
                const group: FormGroup<AccountBalanceRange> = this.formBuilder.group({});
                group.addControl('from', new FormControl<number>(this.tiynToTenge(predefinedValue?.from)));
                group.addControl('to', new FormControl<number>(this.tiynToTenge(predefinedValue?.to)));
                group.addControl('currency', new FormControl<string>(predefinedValue?.currency));
                this.form.addControl(field.name, group);
            } else if (field.type === 'text') {
                this.form.addControl(field.name, new FormControl(predefinedValue));
            }
        });
        this.subscribeToFormInitialization();
    }

    isEditedAmount(): boolean {
        const g = this.form?.controls?.amountRange;
        return !!(g?.dirty || g?.controls?.from?.dirty || g?.controls?.to?.dirty || g?.controls?.currency?.dirty);
    }

    isEditedBalance(): boolean {
        const g = this.form?.controls?.accountBalanceRange;
        return !!(g?.dirty || g?.controls?.from?.dirty || g?.controls?.to?.dirty || g?.controls?.currency?.dirty);
    }

    getSelectedFieldTranslation(field: FilterField): string {
        if (field.translationPrefix) {
            return `i18n_af_filterDialogFieldSelected#${field.translationPrefix}#${field.name}`;
        } else {
            return `i18n_af_filterDialogFieldSelected#${field.name}`;
        }
    }

    getCurrencyDisplay(code: string): string {
        const found = this.currencyDict.find(d => d.code === code);
        return found?.code1 ?? code;
    }

    ngOnDestroy(): void {
        this.form = null;
        this.subscriptionsHolder.forEach(subscription => subscription.unsubscribe());
        this.subscriptionsHolder = [];
    }

    submit(): void {
        const raw = this.form.getRawValue();
        if (this.form.controls.amountRange) {
            raw.amountRange = this.toTiynIfNotEdited(
                this.form.controls.amountRange as any,
                this.isEditedAmount(),
            );
        }
        if (this.form.controls.accountBalanceRange) {
            raw.accountBalanceRange = this.toTiynIfNotEdited(
                this.form.controls.accountBalanceRange as any,
                this.isEditedBalance(),
            );
        }
        this.dialogRef.close(raw);
    }

    selectedValueCount(field: FilterField): number {
        if (field.type === 'select') {
            const controls = (this.form.get(field.name) as FormGroup)?.controls;
            if (!controls) {
                return 0;
            }
            return Object.entries(controls)
                .filter(([_, control]) => (control.value === true || control.value === 'true'))
                .length;
        }
        return 0;
    }

    checkboxChecked(field: FilterField, value: string, event: CheckboxComponent): void {
        const controls = (this.form.get(field.name) as FormGroup).controls;
        const isChecked = event.checked;
        Object.keys(controls).forEach(key => controls[key].reset());
        controls[value].setValue(isChecked);
    }

    clearFilters(): void {
        this.form.reset();
        Object.values(this.searchControls).forEach(control => control.reset());
        this.formattedDisplayedDate = '';
    }

    clearSearchValue(field: FilterField): void {
        this.searchControls[field.name].setValue('');
    }

    filterSelectField<T>(field: FilterField): FilterSelectField<T> {
        if ('values' in field && Array.isArray(field.values)) {
            return field as FilterSelectField<T>;
        } else {
            return null;
        }
    }

    tiynToTenge(value: number | null): number | null {
        return value != null ? parseFloat((value / TIYN_TENGE_SCALE_FACTOR).toFixed(2)) : null;
    }

    private loadFilterValues(
        field: FilterDictionaryField | FilterAsyncField,
        source$: Observable<any[]>,
        filteredValues: string[] | null | undefined,
        searchFields: string[],
        filterBy?: string[],
    ): void {
        this.form.addControl(field.name, new FormControl(null));
        const subscription = source$
            .pipe(rxSpinner(field.name))
            .subscribe(receivedValues => {
            const values = filterBy?.length
                ? receivedValues.filter(item => [item.code, item.code1, item.code2, item.name_ru].some(v => filterBy.includes(v)))
                : receivedValues;
            field.values = values;
            const group = this.formBuilder.group({});
            values.forEach(value => {
                const selectedValue = !!(filteredValues?.length && filteredValues.includes(value.code));
                group.addControl(value.code, new FormControl(selectedValue || null));
            });
            this.form.removeControl(field.name);
            this.form.addControl(field.name, group);
            if (values.length > SEARCHABLE_FIELD_VALUES_LENGTH) {
                this.fieldOriginalValueHolder[field.name] = values;
                this.searchControls[field.name] = new FormControl<string>(null);
                const searchSubscription = this.searchControls[field.name].valueChanges
                    .pipe(debounceTime(500), distinctUntilChanged())
                    .subscribe(searchValue => {
                        field.values = searchValue
                            ? this.fieldOriginalValueHolder[field.name].filter(ref =>
                                searchFields.map(f => ref[f])
                                    .filter(code => !!code)
                                    .some(code => code?.toLowerCase().includes(searchValue.toLowerCase())))
                            : [...this.fieldOriginalValueHolder[field.name]];
                        this.cdr.detectChanges();
                    });
                this.subscriptionsHolder.push(searchSubscription);
            }
        });
        this.subscriptionsHolder.push(subscription);
    }

    private buildDateForm(): FormGroup<DateForm> {
        return this.formBuilder.group<DateForm>({
            date: new FormControl<Date>(null),
            time: new FormControl<string>(null),
        });
    }

    private isFilterSelectField<T>(field: FilterField): field is FilterSelectField<T> {
        return 'values' in field && Array.isArray(field.values);
    }

    private isFilterDictionaryField(field: FilterField): field is FilterDictionaryField {
        return 'dictionaryName' in field;
    }

    private isFilterAsyncField(field: FilterField): field is FilterAsyncField {
        return 'request' in field;
    }

    private subscribeToFormInitialization(): void {
        const subscription = this.form.valueChanges.pipe(
            startWith(null),
            filter(() => this.dialogData.fields.every(field =>
                !(this.isFilterDictionaryField(field) || this.isFilterAsyncField(field))
                || this.form.get(field.name) instanceof FormGroup,
            )),
            take(1),
        ).subscribe(() => {
            setTimeout(() => {
                this.initialFormValue = this.form.getRawValue();
            });
        });
        this.subscriptionsHolder.push(subscription);
    }

    private toTiynIfNotEdited(
        group: FormGroup<{from: FormControl<number | null>, to: FormControl<number | null>, currency: FormControl<string | null>}>,
        edited: boolean,
    ): {from: number | null; to: number | null; currency: string | null} {
        const value = group.getRawValue();
        if (edited) {
            return value;
        }
        const toTiyn = (n: number | null): number | null =>
            n != null ? Math.round(n * TIYN_TENGE_SCALE_FACTOR) : null;
        return {
            ...value,
            from: toTiyn(value.from),
            to: toTiyn(value.to),
        };
    }
}

export interface FilterDialogData {
    fields: FilterField[];
    filter: Record<string, any>;
}
